import React from "react";
import { Box, Text, Tag, SimpleGrid, HStack, Icon } from "@chakra-ui/react";

/**
 * @param {Object} props
 * @param {React.ElementType} props.icon - react-icons component
 * @param {string} props.label - Row label text
 * @param {string|React.ReactNode} props.value - Row value content
 * @param {string} [props.color] - Custom text color for the value
 * @param {"default"|"monospace"|"chip"} [props.variant] - Display variant for the value
 * @param {"flex"|"grid"} [props.layout] - Layout mode: flex (compact) or grid (spaced)
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {Object} [props.sx] - Additional Chakra style props for the root container
 * @returns {React.ReactElement}
 */
function InfoRow({ icon: IconCmp, label, value, color, variant = "default", layout = "flex", isMobile, sx = {} }) {
    if (layout === "grid") {
        return renderGridLayout(IconCmp, label, value, isMobile, sx);
    }

    return renderFlexLayout(IconCmp, label, value, color, variant, isMobile, sx);
}

/**
 * Returns Chakra style props for the grid layout label text.
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {Object} Chakra style props
 */
function gridLabelSx(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.5)",
        fontSize: isMobile ? "0.75rem" : "0.8rem",
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.5px"
    };
}

/**
 * Returns Chakra style props for the grid layout value text.
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {Object} Chakra style props
 */
function gridValueSx(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.8)",
        fontSize: isMobile ? "0.8rem" : "0.85rem",
        fontWeight: 500,
        textAlign: isMobile ? "left" : "right"
    };
}

/**
 * @param {React.ElementType} IconCmp - react-icons component
 * @param {string} label - Row label
 * @param {string|React.ReactNode} value - Row value
 * @param {boolean} isMobile - Mobile viewport flag
 * @param {Object} sx - Additional styles
 * @returns {React.ReactElement} Grid-based layout used by wallet views
 */
function renderGridLayout(IconCmp, label, value, isMobile, sx) {
    return (
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={4} {...sx}>
            <HStack spacing={2} alignItems="center">
                <Icon as={IconCmp} color="#17dcb8" boxSize={isMobile ? "16px" : "18px"} />
                <Text {...gridLabelSx(isMobile)}>
                    {label}
                </Text>
            </HStack>
            <Text {...gridValueSx(isMobile)}>
                {value}
            </Text>
        </SimpleGrid>
    );
}

/**
 * Returns Chakra style props for the flex layout container.
 * @param {boolean} isMobile - Mobile viewport flag
 * @param {Object} sx - Additional styles to merge
 * @returns {Object} Chakra style props
 */
function flexContainerSx(isMobile, sx) {
    return {
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: isMobile ? "6px 10px" : "7px 12px",
        background: "rgba(255, 255, 255, 0.04)",
        backdropFilter: "blur(10px)",
        borderRadius: "8px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        transition: "all 0.3s ease",
        _hover: {
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.12)"
        },
        ...sx
    };
}

/**
 * Returns Chakra style props for the flex layout label text.
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {Object} Chakra style props
 */
function flexLabelSx(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.6)",
        fontSize: isMobile ? "0.65rem" : "0.7rem",
        fontWeight: 600,
        minWidth: isMobile ? "70px" : "80px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        flexShrink: 0
    };
}

/**
 * @param {React.ElementType} IconCmp - react-icons component
 * @param {string} label - Row label
 * @param {string|React.ReactNode} value - Row value
 * @param {string} [color] - Custom text color
 * @param {"default"|"monospace"|"chip"} variant - Display variant
 * @param {boolean} isMobile - Mobile viewport flag
 * @param {Object} sx - Additional styles
 * @returns {React.ReactElement} Compact flex-based layout
 */
function renderFlexLayout(IconCmp, label, value, color, variant, isMobile, sx) {
    return (
        <Box {...flexContainerSx(isMobile, sx)}>
            <Icon as={IconCmp} color="#17dcb8" boxSize={isMobile ? "16px" : "18px"} flexShrink={0} />
            <Text {...flexLabelSx(isMobile)}>
                {label}
            </Text>
            <Box flex={1} textAlign="right">
                {renderFlexValue(value, color, variant, isMobile)}
            </Box>
        </Box>
    );
}

/**
 * Returns Chakra style props for the monospace variant value display.
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {Object} Chakra style props
 */
function monospaceSx(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.9)",
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        fontWeight: 500,
        fontFamily: "monospace",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        padding: "2px 6px",
        borderRadius: "4px",
        display: "inline-block",
        border: "1px solid rgba(255, 255, 255, 0.1)"
    };
}

/**
 * Returns Chakra style props for the chip variant value display.
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {Object} Chakra style props
 */
function chipVariantSx(isMobile) {
    return {
        background: "rgba(23, 220, 184, 0.15)",
        backdropFilter: "blur(10px)",
        color: "#17dcb8",
        border: "1px solid rgba(23, 220, 184, 0.4)",
        fontWeight: 600,
        fontSize: isMobile ? "0.65rem" : "0.7rem",
        height: "22px",
        borderRadius: "6px",
        boxShadow: "0 2px 6px rgba(23, 220, 184, 0.15)"
    };
}

/**
 * @param {string|React.ReactNode} value - The value to render
 * @param {string} [color] - Custom text color
 * @param {"default"|"monospace"|"chip"} variant - Display variant
 * @param {boolean} isMobile - Mobile viewport flag
 * @returns {React.ReactElement} Rendered value element
 */
function renderFlexValue(value, color, variant, isMobile) {
    if (variant === "monospace") {
        return (
            <Text {...monospaceSx(isMobile)}>
                {value || "Non renseigné"}
            </Text>
        );
    }

    if (variant === "chip") {
        return <Tag size="sm" {...chipVariantSx(isMobile)}>{value || "Non renseigné"}</Tag>;
    }

    return (
        <Text
            color={color || "rgba(255, 255, 255, 0.85)"}
            fontSize={isMobile ? "0.75rem" : "0.8rem"}
            fontWeight={500}
            wordBreak="break-word"
        >
            {value || "Non renseigné"}
        </Text>
    );
}

export default InfoRow;
